import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import AuthService from '../../modules/Admin/services/AuthService';
import {
  ensureModuleAccessLoaded,
  isPathModuleAllowed,
  MODULE_ACCESS_STORAGE_KEY,
} from '../utils/moduleAccessStore';
import { ensureSubscriptionChecked } from '../utils/subscriptionAccessStore';
import { Roles } from '../../routes/roles';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const location = useLocation();
  const isAuthenticated = AuthService.isAuthenticated();
  const currentRole = AuthService.getLoggedInRoleId();
  const currentRoleUpper = String(currentRole || '').toUpperCase();
  
  // Check if current role is SuperAdmin
  const isSuperAdmin = currentRoleUpper === Roles.SuperAdmin.toUpperCase();
  
  const needsModuleBootstrap = !isSuperAdmin && !!AuthService.getLoggedInClientId();

  const [modsReady, setModsReady] = useState(
    () => !needsModuleBootstrap || !!sessionStorage.getItem(MODULE_ACCESS_STORAGE_KEY)
  );
  const [subReady, setSubReady] = useState(() => isSuperAdmin);
  const [subActive, setSubActive] = useState(true);

  useEffect(() => {
    if (!needsModuleBootstrap) return undefined;
    let cancelled = false;
    ensureModuleAccessLoaded().then(() => {
      if (!cancelled) setModsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [needsModuleBootstrap]);

  useEffect(() => {
    if (isSuperAdmin) return undefined;
    let cancelled = false;
    ensureSubscriptionChecked().then((active) => {
      if (!cancelled) {
        setSubActive(active);
        setSubReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Check if current role is in allowedRoles (compare GUIDs)
  const isAllowed = allowedRoles.some(allowedRole => 
    String(allowedRole || '').toUpperCase() === currentRoleUpper
  );
  
  if (!isAllowed) {
    return <Navigate to="/unauthorized" />;
  }

  if (!modsReady || !subReady) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#8392ab', fontSize: '14px' }}>
        <i className="fas fa-circle-notch fa-spin me-2" />
        Loading workspace…
      </div>
    );
  }

  if (!isSuperAdmin && !subActive) {
    return <Navigate to="/subscription-inactive" replace />;
  }

  if (needsModuleBootstrap && !isPathModuleAllowed(location.pathname)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;
